import * as RadixCheckbox from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { useId, type ComponentPropsWithoutRef, type ReactNode } from 'react';

import { cx } from '@/shared/lib/cx';

import styles from './Checkbox.module.scss';

export type CheckboxSize = 'sm' | 'md';

const SIZE: Record<CheckboxSize, string> = {
  sm: styles['checkbox--sm'] ?? '',
  md: styles['checkbox--md'] ?? '',
};

type RadixCheckboxProps = ComponentPropsWithoutRef<typeof RadixCheckbox.Root>;

export interface CheckboxProps extends RadixCheckboxProps {
  size?: CheckboxSize;
  /** Подпись рядом с флажком. Без неё обязателен `aria-label`. */
  children?: ReactNode;
}

/**
 * Флажок поверх примитива Radix.
 *
 * Свой нужен из-за третьего состояния. Заголовок таблицы с выбором строк не
 * бывает просто «включён» или «выключен»: когда отмечена часть строк, он
 * должен быть «частично», и это не оттенок цвета, а `aria-checked="mixed"` —
 * нативный `input` такого значения не знает вовсе, его выставляют
 * свойством из JS, и оно теряется при каждой перерисовке.
 *
 * Подпись связана через `for`/`id`, а не вложением: элемент управления здесь
 * не `input`, а кнопка, и попадание по подписи работает только так.
 */
export function Checkbox({ size = 'md', className, children, id, ...rest }: CheckboxProps) {
  const generatedId = useId();
  const controlId = id ?? generatedId;

  const control = (
    <RadixCheckbox.Root
      id={controlId}
      className={cx(styles['checkbox'], SIZE[size], children === undefined && className)}
      {...rest}
    >
      <RadixCheckbox.Indicator className={styles['checkbox__mark']}>
        {rest.checked === 'indeterminate' ? <Minus strokeWidth={3} /> : <Check strokeWidth={3} />}
      </RadixCheckbox.Indicator>
    </RadixCheckbox.Root>
  );

  if (children === undefined) {
    return control;
  }

  return (
    <div className={cx(styles['field'], className)}>
      {control}
      <label className={styles['field__label']} htmlFor={controlId}>
        {children}
      </label>
    </div>
  );
}
